import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

import http from 'src/api/endpoints';
import { PARENT_URL } from 'src/config/parentURL';
import Projects from './components/Projects';
import Members from './components/Members';
import useLoading from 'src/hooks/useLoading';
import { getAllProjects } from 'src/redux/projectSlice';
import { AppDispatch, RootState } from 'src/redux/store';
import { getAllFilters } from 'src/redux/filterSlice';

const TeamBuilder = () => {
  const dispatch: AppDispatch = useDispatch();
  const [allMembers, setAllMembers] = useState([]);
  const {
    isLoading: isMemberLoading,
    startLoading: memberStartLoading,
    stopLoading: memberStopLoading,
  } = useLoading();
  const {
    projects: { data: allProjectData, loading: isProjectsLoading },
    filters: { data: filtersData },
  } = useSelector((state: RootState) => state);
  const { statuses, filterRoles: memberFilterRoles } = filtersData || {};
  const { Project: projectStatusFilter, Staffing: staffingStatusFilter } =
    statuses || {};

  const iterableProjectStatusFilters = useMemo(() => {
    const data = projectStatusFilter?.map((item: any) => ({
      label: item.name,
      value: item.rank,
    }));
    data.unshift({
      label: 'All',
      value: 'all',
    });
    return data;
  }, [projectStatusFilter]);

  const iterableStaffingStatusFilters = useMemo(() => {
    const data = staffingStatusFilter?.map((item: any) => ({
      label: item.name,
      value: item.rank,
    }));
    data.unshift({
      label: 'All',
      value: 'all',
    });
    return data;
  }, [staffingStatusFilter]);

  const iterableMemberRolesFilters = useMemo(() => {
    const data = memberFilterRoles?.map((item: any) => ({
      label: item.name,
      value: item.name,
    }));
    data.unshift({
      label: 'All',
      value: 'all',
    });
    return data;
  }, [memberFilterRoles]);

  const iterableProjectName = useMemo(() => {
    const data = allProjectData?.map((item: any) => ({
      label: item.projectCode,
      value: item.projectCode?.toLowerCase(),
    }));
    return data;
  }, [allProjectData]);

  const iterableMemberName = useMemo(() => {
    const data = allMembers?.map((item: any) => ({
      label: item.name,
      value: item.name?.toLowerCase(),
    }));
    return data;
  }, [allMembers]);

  const iterableProjectStaffingStatus = [
    {
      label: 'All',
      value: '',
    },
    {
      label: 'Pending',
      value: 'false',
    },
    {
      label: 'Completed',
      value: 'true',
    },
  ];

  useEffect(() => {
    (async () => {
      try {
        dispatch(getAllProjects());
        dispatch(getAllFilters());
      } catch (error) {
        console.log(error);
        toast.error('Something went wrong. Please try again later.');
      }
    })();
  }, [dispatch]);

  const getMembers = async () => {
    try {
      memberStartLoading();
      const response = await http.getAllMembers(PARENT_URL.TEAM_BUILDER_BASE);
      if (response.message === 'The request is completed successfully') {
        setAllMembers(response?.data?.members);
        memberStopLoading();
        return;
      }
      toast.error('Failed to fetch Members. Please try again later.');
    } catch (error) {
      console.log(error);
      toast.error('Failed to fetch Members. Please try again later.');
    } finally {
      memberStopLoading();
    }
  };

  useEffect(() => {
    getMembers();
  }, []);

  return (
    <div className="flex gap-5 xl:flex-nowrap flex-wrap">
      <div className="flex flex-col gap-4 xl:w-[70%] w-full">
        <Projects
          allProjects={allProjectData}
          isProjectsLoading={isProjectsLoading}
          projectStatusFilter={iterableProjectStatusFilters}
          allProjectName={iterableProjectName}
          projectStaffingStatus={iterableProjectStaffingStatus}
        />
      </div>
      <div className="flex flex-col gap-4 xl:w-[30%] w-full">
        <Members
          allMembers={allMembers}
          isMembersLoading={isMemberLoading}
          staffingStatus={iterableStaffingStatusFilters}
          memberRolesFilters={iterableMemberRolesFilters}
          allMemberName={iterableMemberName}
        />
      </div>
    </div>
  );
};

export default TeamBuilder;
